import { useCallback, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ResetFiltersButton } from "components/core/ResetFiltersButton.tsx";
import { EquipmentTypeMultiSelect } from "components/EquipmentTypeSelect";
import { ProvinceStateMultiSelect } from "components/ProvinceStateMultiSelect";
import { ShipmentServiceMultiSelect } from "components/ShipmentServiceMultiSelect";
import { AppPage, ClientAppEvent } from "constants/index";
import { useDisclosure, useRoleContext } from "hooks";
import { useShipmentFiltersContext } from "hooks/useShipmentFiltersContext";
import { useSwrFetch } from "hooks/useSwrFetch";
import { getActiveFiltersCount } from "utils";

import { CreateLanesModal } from "./components/CreateLanesModal";
import { LaneSelectorModal } from "./components/LaneSelectorModal";

type FilterIdentifier = "equipment_type" | "service" | "origin" | "destination";

interface Props {
  onShipmentSearchCreated?: VoidFunction;
  currentPage?: AppPage;
}

export function ShipmentsFilter(props: Props) {
  const {
    onShipmentSearchCreated = () => {},
    currentPage = AppPage.DASHBOARD,
  } = props;

  const posthog = usePostHog();
  const { role } = useRoleContext();
  const {
    filters: currentFilters,
    clearFilters,
    updateOrigin,
    updateDestination,
    updateEquipmentType,
    updateServiceType,
  } = useShipmentFiltersContext();
  const [selectedLane, setSelectedLane] = useState<string>("");
  const {
    isOpen: laneModalOpen,
    onOpen: openLaneModal,
    onClose: closeLaneModal,
  } = useDisclosure();
  const {
    isOpen: createLanesModalOpen,
    onOpen: openCreateLanesModal,
    onClose: closeCreateLanesModal,
  } = useDisclosure();
  const {
    isOpen: toastOpen,
    onOpen: openToast,
    onClose: closeToast,
  } = useDisclosure();

  const {
    data: savedLanes = [],
    loading: lanesLoading,
    error: lanesError,
    refetch: reloadLanes,
  } = useSwrFetch<ShipmentSearch[]>({
    url: role?.company ? `/carriers/${role.id}/lanes` : null,
    errorMessage: "Failed to load saved lanes",
  });

  const handleLaneSelected = (lane: ShipmentSearch) => {
    setSelectedLane(lane.id.toString());
    updateEquipmentType(lane.equipment_types ?? []);
    updateServiceType(lane.services ?? []);
    updateOrigin("provinces", lane.origin_provinces ?? []);
    updateDestination("provinces", lane.destination_provinces ?? []);
    const totalFilterCount =
      (lane.equipment_types?.length ?? 0) +
      (lane.services?.length ?? 0) +
      (lane.origin_provinces?.length ?? 0) +
      (lane.destination_provinces?.length ?? 0);
    posthog?.capture(ClientAppEvent.CARRIER_LANE_TEMPLATE_APPLIED, {
      template_id: lane.id,
      template_name: lane.name,
      equipment_types: lane.equipment_types ?? [],
      services: lane.services ?? [],
      origin_provinces: lane.origin_provinces ?? [],
      destination_provinces: lane.destination_provinces ?? [],
      equipment_type_count: lane.equipment_types?.length ?? 0,
      service_count: lane.services?.length ?? 0,
      origin_province_count: lane.origin_provinces?.length ?? 0,
      destination_province_count: lane.destination_provinces?.length ?? 0,
      total_filter_count: totalFilterCount,
    });
    openToast();
  };

  const handleLaneDeleted = (deletedLaneId: number) => {
    if (selectedLane === deletedLaneId.toString()) {
      setSelectedLane("");
    }
    reloadLanes();
  };

  const handleOpenLaneSelector = () => {
    posthog?.capture(ClientAppEvent.CARRIER_FILTER_BUTTON_CLICKED, {
      active_filters_count: getActiveFiltersCount(currentFilters),
      applied_filters: {
        equipment_types: currentFilters?.equipment_types ?? [],
        services: currentFilters?.services ?? [],
        origin_provinces: currentFilters?.origin?.provinces ?? [],
        destination_provinces: currentFilters?.destination?.provinces ?? [],
      },
    });
    openLaneModal();
  };

  const captureFilterSelected = useCallback(
    (filterIdentifier: FilterIdentifier) => {
      posthog?.capture(
        ClientAppEvent.CARRIER_DASHBOARD_SHIPMENT_FILTER_SELECTED,
        {
          current_page: currentPage,
          filter_identifier: filterIdentifier,
        },
      );
    },
    [posthog, currentPage],
  );

  const handleEquipmentTypeChange = useCallback(
    (value: string[]) => {
      updateEquipmentType(value);
      setSelectedLane("");
      captureFilterSelected("equipment_type");
    },
    [updateEquipmentType, captureFilterSelected],
  );

  const handleServiceTypeChange = useCallback(
    (value: string[]) => {
      updateServiceType(value);
      setSelectedLane("");
      captureFilterSelected("service");
    },
    [updateServiceType, captureFilterSelected],
  );

  const handleOriginChange = useCallback(
    (value: string[]) => {
      updateOrigin("provinces", value);
      setSelectedLane("");
      captureFilterSelected("origin");
    },
    [updateOrigin, captureFilterSelected],
  );

  const handleDestinationChange = useCallback(
    (value: string[]) => {
      updateDestination("provinces", value);
      setSelectedLane("");
      captureFilterSelected("destination");
    },
    [updateDestination, captureFilterSelected],
  );

  const handleClearFilters = () => {
    clearFilters();
    setSelectedLane("");
  };

  const hasActiveFilters = useMemo(() => {
    if (selectedLane) return true;
    return (
      (currentFilters?.equipment_types?.length ?? 0) > 0 ||
      (currentFilters?.services?.length ?? 0) > 0 ||
      (currentFilters?.origin?.provinces?.length ?? 0) > 0 ||
      (currentFilters?.destination?.provinces?.length ?? 0) > 0
    );
  }, [selectedLane, currentFilters]);

  const filterStatusText = useMemo((): string | null => {
    if (selectedLane) {
      const laneName = savedLanes.find(
        (l) => l.id.toString() === selectedLane,
      )?.name;
      return laneName ? `Lane applied: ${laneName}` : "Lane applied";
    }
    if (!hasActiveFilters) {
      return "Currently showing shipments in all lanes";
    }
    return null;
  }, [selectedLane, savedLanes, hasActiveFilters]);

  return (
    <Box>
      <Grid container spacing={1.2}>
        <Grid item xs={12} sm={6} md>
          <Box sx={{ position: "relative" }}>
            <FormControl fullWidth aria-hidden>
              <Select
                value={selectedLane}
                open={false}
                displayEmpty
                SelectDisplayProps={{ tabIndex: -1 }}
              >
                <MenuItem value="">
                  <em>Select a lane...</em>
                </MenuItem>
                {savedLanes.map((lane) => (
                  <MenuItem key={lane.id} value={lane.id.toString()}>
                    {lane.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              role="button"
              aria-label="Lanes"
              aria-haspopup="dialog"
              aria-expanded={laneModalOpen}
              tabIndex={0}
              sx={{
                position: "absolute",
                inset: 0,
                cursor: "pointer",
                zIndex: 1,
              }}
              onClick={handleOpenLaneSelector}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md>
          <EquipmentTypeMultiSelect
            onChange={handleEquipmentTypeChange}
            defaultValue={currentFilters?.equipment_types ?? []}
            value={currentFilters?.equipment_types ?? []}
            width="100%"
          />
        </Grid>
        <Grid item xs={12} sm={6} md>
          <ShipmentServiceMultiSelect
            label="Services"
            value={currentFilters?.services ?? []}
            onChange={handleServiceTypeChange}
            width="100%"
          />
        </Grid>
        <Grid item xs={6} md>
          <ProvinceStateMultiSelect
            label="Origins"
            value={currentFilters?.origin?.provinces ?? []}
            onChange={handleOriginChange}
            width="100%"
          />
        </Grid>
        <Grid item xs={6} md>
          <ProvinceStateMultiSelect
            label="Destinations"
            value={currentFilters?.destination?.provinces ?? []}
            onChange={handleDestinationChange}
            width="100%"
          />
        </Grid>
      </Grid>
      {lanesError && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {lanesError}
        </Typography>
      )}
      <Box sx={{ mt: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Create new lanes to be notified when matching shipments are posted">
            <Button
              color="primary"
              onClick={() => {
                posthog?.capture(
                  ClientAppEvent.CARRIER_CREATE_LANES_BUTTON_CLICKED,
                );
                openCreateLanesModal();
              }}
              sx={{ textTransform: "none", pl: 0, flexShrink: 0 }}
            >
              + Create Lanes
            </Button>
          </Tooltip>
          {filterStatusText && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontStyle: "italic",
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {filterStatusText}
            </Typography>
          )}
          {hasActiveFilters && (
            <ResetFiltersButton handleResetFilters={handleClearFilters} />
          )}
        </Stack>
      </Box>
      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={closeToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={closeToast} severity="success" sx={{ width: "100%" }}>
          Saved lane filters applied successfully!
        </Alert>
      </Snackbar>
      <LaneSelectorModal
        open={laneModalOpen}
        onClose={closeLaneModal}
        lanes={savedLanes}
        lanesLoading={lanesLoading}
        selectedLaneId={selectedLane}
        onLaneSelected={handleLaneSelected}
        onLaneDeleted={handleLaneDeleted}
        onLaneUpdated={reloadLanes}
      />
      <CreateLanesModal
        open={createLanesModalOpen}
        onClose={closeCreateLanesModal}
        onLanesCreated={() => {
          reloadLanes();
          onShipmentSearchCreated();
        }}
      />
    </Box>
  );
}
